import React, { useEffect, useState } from "react";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PuffLoader } from "react-spinners";
import signinImage from "../assets/images/signin_image.png";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import API_ENDPOINTS from "../config/api";

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [obscureText, setObscureText] = useState(true);
  const [loading, setLoading] = useState(false); // State for loader
  const [bannedMessage, setBannedMessage] = useState(""); // State for banned message
  const { signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const disableRightClick = (e) => e.preventDefault();
    document.addEventListener("contextmenu", disableRightClick);

    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
    };
  }, []);

  // Function to validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFormValid = isValidEmail(email) && password.trim() !== ""; // Check for valid email and non-empty password

  const handleSignIn = async () => {
    if (!isFormValid) {
      toast.warn("Please enter a valid email and password.");
      return;
    }

    setLoading(true); // Start loader
    try {
      const response = await axios.post(
        API_ENDPOINTS.SIGNIN,
        {
          email,
          password,
        }
      );

      setLoading(false); // Stop loader

      if (response.status === 200 && response.data.token) {
        signIn({
          token: response.data.token,
          fullname: response.data.fullname,
          account: response.data.account,
          phoneNumber: response.data.phoneNumber,
          role: response.data.role,
        });
        toast.success("Sign in successful!");
        navigate("/home");
      } else {
        toast.error("Sign in failed.");
      }
    } catch (error) {
      setLoading(false); // Stop loader
      console.error("Error during sign in:", error);

      // Display specific error message from backend
      // Show persistent banned message for 403 responses
      if (error.response?.status === 403) {
        setBannedMessage(error.response.data.error);
        return;
      }

      const errorMessage = error.response?.data?.error || "Sign in failed. Please try again.";
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {loading ? (
        <div className="flex justify-center items-center min-h-screen">
          <PuffLoader color="#008080" size={50} />
        </div>
      ) : (
        <div className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg bg-white rounded-lg shadow-md p-6 text-center">
          <img
            src={signinImage}
            alt="Sign In"
            className="mx-auto mb-6 w-20 sm:w-24 md:w-28 lg:w-32"
          />

          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 text-center">
            Sign In
          </h1>

          {bannedMessage && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 rounded-md">
              <p className="text-red-700 text-sm font-semibold text-center">
                {bannedMessage}
              </p>
            </div>
          )}

          <div className="mb-4">
            <TextField
              id="email"
              label="Email"
              name="email"
              variant="outlined"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AlternateEmailOutlinedIcon sx={{ color: "#008080" }} />
                  </InputAdornment>
                ),
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#000000",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "teal",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "teal",
                  },
                },
              }}
              InputLabelProps={{
                sx: { color: "#008080", "&.Mui-focused": { color: "#008080" } },
              }}
              sx={{ width: "100%" }}
            />
          </div>

          <div className="mb-4">
            <TextField
              id="password"
              name="password"
              label="Password"
              type={obscureText ? "password" : "text"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: "#008080" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setObscureText(!obscureText)}
                      edge="end"
                    >
                      {obscureText ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#000000",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "teal",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "teal",
                  },
                },
              }}
              InputLabelProps={{
                sx: { color: "#008080", "&.Mui-focused": { color: "#008080" } },
              }}
              sx={{ width: "100%" }}
            />
          </div>

          <button
            onClick={handleSignIn}
            disabled={!isFormValid} // Disable button if form is invalid
            className={`w-full px-3 py-2 md:px-4 md:py-3 rounded-md text-sm md:text-lg font-semibold transition ${
              isFormValid
                ? "bg-teal-600 text-white hover:bg-teal-700"
                : "bg-gray-400 text-gray-700 cursor-not-allowed"
            }`}
          >
            Sign In
          </button>

          <button
            onClick={() => navigate("/signup")}
            className="w-full mt-4 text-teal-600 text-xs md:text-sm hover:text-teal-800 transition"
          >
            Don't have an account? Sign up
          </button>

          <button
            onClick={() => navigate("/resetpassword")}
            className="w-full mt-1 text-teal-600 text-xs md:text-sm hover:text-teal-800 transition"
          >
            Forgot your password?
          </button>
          
        </div>
      )}
    </div>
  );
};

export default SignIn;